import { useContext, useState } from 'react'
import UserList from './UserList'
import ChangeLogoDialog from './change-logo/ChangeLogoDialog'
import { AuthContext } from '@/context/auth-context'
import { deleteOrgLogo } from '../services/org-logo-services'
import { Button } from '@/components/button/Button'
import ErrorComponent from '@/components/error/error-component'
import { getBrandFromHost, getLogoFromBrand } from '@/util/enterprise'
import { Header } from '@/components/header/Header'

const hostBrand = getBrandFromHost()
const brandLogoUrl = getLogoFromBrand(hostBrand)

export default function AdminDashboardPage() {
  const { logoUrl, userAccountData } = useContext(AuthContext)

  // Local state
  const [changeLogoDialogVisible, setChangeLogoDialogVisible] = useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Inferred values
  const orgName = userAccountData?.orgData?.name ?? 'Your Organization'
  const orgId = userAccountData?.legacyDriveCompatibleOrganizationId
  const isOrgAdmin = userAccountData?.isOrgAdmin

  // Handle delete logo
  const handleDeleteLogo = async () => {
    if (!orgId) {
      setDeleteError('No organization id found.')
      return
    }

    try {
      setDeleteLoading(true)
      await deleteOrgLogo(userAccountData?.legacyDriveCompatibleOrganizationId)

      // Refresh the screen to see the update
      window.location.reload()
    } catch (e) {
      console.error('Could not delete org logo. Error: ', e)
      setDeleteError('Could not delete the logo.')
    } finally {
      setDeleteLoading(false)
    }
  }

  if (!isOrgAdmin) {
    return <ErrorComponent message={'You must be an organization admin to access this screen.'} code={'403'} title={'Access Denied'} />
  }

  return (
    <>
      <ChangeLogoDialog
        visible={changeLogoDialogVisible}
        onComplete={() => {
          setChangeLogoDialogVisible(false)

          // Refresh the screen to see the logo
          window.location.reload()
        }}
        onClose={() => setChangeLogoDialogVisible(false)}
      />

      <div className="flex flex-col flex-1 min-h-0">
        <Header title="Admin Dashboard" borderBottom />

        {/* Admin Dashboard Content */}
        <div className="flex-1 min-h-0 p-4 lg:px-10 lg:py-8 overflow-y-auto">
          <div className="flex flex-col grow items-center rounded-md pt-4 mb-4">
            <img className="max-h-[100px] max-w-[200px] mb-1" src={logoUrl} alt={hostBrand}></img>
            <div className={'flex gap-x-4'}>
              <Button variant="plain" color="primary" size="xs" className="mb-4" onClick={() => setChangeLogoDialogVisible(true)}>
                Change Logo
              </Button>
              {logoUrl != brandLogoUrl && (
                <Button variant="plain" color="destructive" size="xs" className="mb-4" loading={deleteLoading} onClick={handleDeleteLogo}>
                  Delete Logo
                </Button>
              )}
            </div>
            {deleteError && <p className={'text-red-600 text-s mb-4'}>{deleteError}</p>}
            <h1 className={'font-bold text-xl'}>{orgName}</h1>
            <p className={'text-sm mb-6'}>Manage your organization settings and team.</p>
          </div>
          <UserList />
          <div className={'h-10'}> </div>
        </div>
      </div>
    </>
  )
}
